import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { BellIcon } from '@phosphor-icons/react';
import paths from '@/paths';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useUnreadNotificationsCount,
  useMarkAllNotificationsAsRead,
} from '@/api/rpc/hooks/communications/useNotifications';
import { useIdleTimeout } from './useIdleTimeout';
import { NotificationList } from './NotificationList';

const IDLE_MS = 5 * 60 * 1000;
const BADGE_CAP = 99;

export function NotificationBell() {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const inboxFlag = useFeatureFlag('inbox-enabled', false);
  // Polling pauses after 5 min idle and whenever the tab is hidden.
  // React Query's refetchOnWindowFocus picks up missed notifications the
  // instant the user returns, so the trade-off is invisible in practice.
  const isIdle = useIdleTimeout(IDLE_MS);
  const { data } = useUnreadNotificationsCount({ isIdle });
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const count = data?.count ?? 0;
  const badge = count > BADGE_CAP ? `${BADGE_CAP}+` : String(count);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              aria-label={gt('Notifications', {
                context: 'aria label for the notification bell icon button',
              })}
            >
              <BellIcon className="h-4 w-4" weight="regular" />
              {count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium leading-none text-white">
                  {badge}
                </span>
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <T context="bell icon tooltip">Notifications</T>
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        align="end"
        className="w-96 p-0"
        onClick={(event) => {
          if (
            event.target instanceof Element &&
            event.target.closest('a[href]')
          ) {
            setOpen(false);
          }
        }}
      >
        <div className="flex items-center justify-between border-b px-3 py-2">
          <div className="text-sm font-medium">
            <T context="notification inbox header">Notifications</T>
          </div>
          {count > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => markAllAsRead.mutate({})}
              disabled={markAllAsRead.isPending}
            >
              <T context="mark all notifications as read">Mark all read</T>
            </Button>
          )}
        </div>
        <NotificationList />
        <div className="border-t px-3 py-2">
          <Link
            to={
              inboxFlag.enabled
                ? paths.authenticated.inbox
                : paths.authenticated.notifications
            }
            search={inboxFlag.enabled ? { filter: 'notifications' } : undefined}
            onClick={() => setOpen(false)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            <T context="link from bell popover to full notifications history page">
              View all notifications →
            </T>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
